/**
 * Drag support shared by both boards — V5-02 §2.2/§2.3, reused by V6-03 §3.
 *
 * The project board and the all-projects board must behave identically under
 * a pointer, a finger and a keyboard. Two copies of these sensors would drift,
 * and the drift would show up as "drag works on one board and not the other",
 * which is precisely the class of bug V5-02 was written to close.
 */

import { useCallback } from 'react';
import {
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core';
import { EditableAwareKeyboardSensor } from '@/lib/dndSensors';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { PERMISSIONS, grantFor } from '@virtuetasks/types';
import { useAuth } from '@/features/auth';
import type { TaskView } from './api';

/**
 * Keyboard drag across a board — V5-02 §2.2.
 *
 * `sortableKeyboardCoordinates` navigates between sortable *items*, which on a
 * board means ArrowRight usually lands on another card in the same column: the
 * card lifts, the arrow appears to do nothing, and the drop is a no-op. The
 * left/right axis on a board means "next column", so it is handled here and
 * up/down is left to the sortable strategy.
 *
 * `columnIds` is in display order, so stepping is by position rather than by
 * geometry — which stays correct when a column is collapsed or scrolled out of
 * view.
 */
export function boardKeyboardCoordinates(columnIds: string[]): KeyboardCoordinateGetter {
  return (event, args) => {
    const { currentCoordinates, context } = args;
    const horizontal = event.code === 'ArrowRight' || event.code === 'ArrowLeft';
    if (!horizontal) return sortableKeyboardCoordinates(event, args);

    event.preventDefault();

    const containers = context.droppableContainers;
    const currentIndex = columnIds.findIndex((id) => {
      const rect = containers.get(id)?.rect.current;
      if (!rect) return false;
      return currentCoordinates.x >= rect.left && currentCoordinates.x <= rect.left + rect.width;
    });

    const step = event.code === 'ArrowRight' ? 1 : -1;
    const from = currentIndex === -1 ? 0 : currentIndex;
    const target = columnIds[from + step];
    const rect = target ? containers.get(target)?.rect.current : undefined;
    if (!rect) return undefined;

    // Land near the top of the column: a drop there reads as "put it in this
    // column" rather than at whatever vertical offset the previous column had.
    return { x: rect.left + rect.width / 2, y: rect.top + 60 };
  };
}

export function useBoardSensors(columnIds: string[]) {
  return useSensors(
    // 5px before a press becomes a drag, so a click still opens the task
    // (V5-02 §2.2). Below this, opening a card by tapping it becomes a lottery.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    // Touch needs a press-and-hold instead of a distance: a finger always
    // moves a few pixels, so a distance constraint turns every attempt to
    // scroll the board into a drag. 200ms hold, 5px tolerance during it.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    // Keyboard drag: Space to lift, arrows to move, Space to drop (§8.7).
    // V10 §7 — and never when the keystroke came from an editable element.
    useSensor(EditableAwareKeyboardSensor, {
      coordinateGetter: boardKeyboardCoordinates(columnIds),
    }),
  );
}

/**
 * Can this viewer move this particular card? — V5-02 §2.3.
 *
 * Evaluated with the same `grantFor` table the server uses, so the affordance
 * and the API agree. This is a hint, never a control: the server re-checks
 * every move, and a wrong answer here costs a tooltip, not a permission.
 */
export function useCanMoveTask(): (task: TaskView) => boolean {
  const { user, permissions } = useAuth();

  return useCallback(
    (task: TaskView) => {
      if (!user) return false;
      if (!permissions.includes(PERMISSIONS.TASK_CHANGE_STATUS)) return false;

      const grant = grantFor(user.systemRole, PERMISSIONS.TASK_CHANGE_STATUS);
      if (grant === 'all') return true;

      const isOwner = String(task.reporterId ?? '') === user._id;
      const isAssignee = (task.assigneeIds ?? []).map(String).includes(user._id);

      switch (grant) {
        case 'own':
          return isOwner;
        case 'own_assigned':
        case 'assigned':
          return isOwner || isAssignee;
        default:
          // Anything time-boxed or unrecognised: let the server decide rather
          // than grey out a card the viewer may well be allowed to move.
          return true;
      }
    },
    [user, permissions],
  );
}

/** The sentence both boards say when a card cannot be moved. */
export const CANNOT_MOVE = 'You can only move tasks assigned to you';
